import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, TableRow, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import {
    removeSpacesAtStart,
    removeUnderscoreToCamelCase,
} from 'src/modules/common/application/string';
import useDate from 'src/modules/common/application/use-date';
import { UserNameAvatar } from 'src/modules/common/components';
import { Activity } from 'src/modules/common/types';

import { getActivityRoutes } from '../../application/getActivityRoutes';
import { StyledTableCell } from './styles';

const textStyles = { color: 'text.secondary', fontSize: '0.875rem !important' };

const ActivityTableRow = ({
  index,
  data,
  showActionId,
  setShowActionId,
}: {
  index: number;
  data: Activity;
  showActionId: string;
  setShowActionId: (id: string) => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, type, dateTime, payload } = data;
  const { firstName, lastName, email } = user;
  const { getProperFormattedDate } = useDate();

  const formattedDate = getProperFormattedDate(dateTime);

  const payloadObj = payload.reduce<Record<string, string>>((acc, item) => {
    if (item.title === 'esgCategory') {
      acc[item.title] = t(
        `shared.types.esgCategory.${removeUnderscoreToCamelCase(item.value || '')}`
      );
    } else if (item.title === 'chapterTitle') {
      acc[item.title] = t(`shared.types.reportSectionChapter.${item.value}`);
    } else if (item.title === 'kpi') {
      acc[item.title] = t(`shared.types.kpi.${removeUnderscoreToCamelCase(item.value || '')}`);
    } else if (item.title === 'materialTopic') {
      acc[item.title] = t(
        `shared.types.materialTopic.${removeUnderscoreToCamelCase(item.value || '')}`
      );
    } else {
      acc[item.title] = item.value || '';
    }
    return acc;
  }, {});

  const displayName: string =
    firstName && lastName
      ? `${removeSpacesAtStart(firstName)} ${removeSpacesAtStart(lastName)}`
      : (email as string);

  const handleMouseEnter = () => {
    setShowActionId(index.toString());
  };

  const handleMouseLeave = () => {
    setShowActionId('');
  };

  const handleArrowClick = () => {
    navigate(getActivityRoutes(type, payloadObj));
  };

  return (
    <TableRow onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {firstName ? (
        <StyledTableCell>
          <UserNameAvatar text={displayName} />
        </StyledTableCell>
      ) : (
        <StyledTableCell>
          <Typography textAlign="center">{'-'}</Typography>
        </StyledTableCell>
      )}
      <StyledTableCell>
        <Typography variant="body2" sx={textStyles}>
          <Trans
            i18nKey={`shared.types.activity.${removeUnderscoreToCamelCase(type)}`}
            values={payloadObj}
          />
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Typography variant="body2" sx={textStyles}>
          {formattedDate}
        </Typography>
      </StyledTableCell>
      <StyledTableCell sx={{ width: '70px' }}>
        {index.toString() === showActionId && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ArrowForwardIcon color="primary" cursor="pointer" onClick={handleArrowClick} />
          </Box>
        )}
      </StyledTableCell>
    </TableRow>
  );
};

export default ActivityTableRow;
